import { useCallback, useEffect, useRef, useState } from 'react'
import { explain } from '@/shared/api/problem'

/**
 * Small load-once helper so every tab has the same loading/error shape.
 *
 * ## Why there is a sequence guard
 *
 * Every call is stamped with a monotonically increasing token, and a response
 * is only written if it is still the newest one outstanding. Without it, any
 * screen where the user can change the query faster than the network answers
 * will eventually render the wrong data under the right control: click
 * 24h → 7d → 30d on a report and whichever request happens to return last wins,
 * so you can be looking at the 24-hour numbers with "30 days" highlighted. The
 * same applies to paging a cursor list quickly, or switching between rows in a
 * drawer.
 *
 * The guard is on the *write*, not the request — nothing is aborted, because a
 * response that arrives late is harmless as long as it is ignored.
 */
export function useLoad<T>(fn: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const seq = useRef(0)
  // Nothing may be written after unmount — a late response setting state on a
  // gone component is the classic React warning and, worse, a leak.
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])

  const run = useCallback(async () => {
    const mine = ++seq.current
    setLoading(true)
    setError(null)
    try {
      const result = await fn()
      if (alive.current && seq.current === mine) setData(result)
    } catch (err) {
      if (alive.current && seq.current === mine) setError(explain(err))
    } finally {
      if (alive.current && seq.current === mine) setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  /** Silent refetch — updates data without flipping `loading` (auto-save). */
  const refresh = useCallback(async () => {
    const mine = ++seq.current
    setError(null)
    try {
      const result = await fn()
      if (alive.current && seq.current === mine) setData(result)
    } catch (err) {
      if (alive.current && seq.current === mine) setError(explain(err))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  useEffect(() => { void run() }, [run])
  return { data, error, loading, reload: run, refresh }
}
